import { useCallback, useEffect, useState } from 'react';
import { themeConfigs } from '@kss/theme';

/**
 * QR foreground colour preference — shared by the single-product QR card
 * and the bulk A4 label sheet.
 *
 * Why a preference and not a per-product column
 * ---------------------------------------------
 * The colour is a *print* choice, not product data: the same product may
 * be printed on black-ink shelf labels today and brand-coloured labels
 * for a showroom run tomorrow. Persisting it per product would mean a
 * schema change plus a migration for something the admin re-picks per
 * print run anyway. Storing it as one tenant-local preference keeps the
 * single card and the bulk sheet in agreement — pick the colour on the
 * product page, hit "Print QR" from the grid, and the sheet matches.
 *
 * The palette is the 15 accent themes from `@kss/theme` (`themeConfigs`)
 * so the QR can be brand-matched to whatever accent the panel is running,
 * plus `black` (the scanning-safe default) and `theme` (follow whatever
 * `--color-primary` currently resolves to, so re-theming the panel
 * re-colours the QR with no extra click).
 */

const STORAGE_KEY = 'kss.qr.fgColor';

/** Sentinel meaning "resolve from the live `--color-primary` CSS var". */
export const QR_COLOR_THEME = 'theme';
/** Sentinel for the default, maximum-contrast foreground. */
export const QR_COLOR_BLACK = 'black';

export interface QrColorOption {
  /**
   * Stored value — a sentinel, or an accent **theme name** (not its hex).
   *
   * Keyed by name rather than colour because `default` and `warm` both
   * render antique gold `#8b6f47`: storing the hex would make the two
   * indistinguishable, so picking one would highlight both swatches and
   * collide as a React key. Names also survive a palette tweak — if a
   * theme's `previewColor` is retuned, existing preferences follow it
   * instead of pinning the old hex.
   */
  value: string;
  label: string;
  /** Swatch colour shown in the picker. */
  swatch: string;
}

/**
 * Picker options: Black + Theme first (the two scanning-safe defaults),
 * then all 15 accent themes in `themeConfigs` order, so the list reads
 * the same as the theme switcher the admin already knows.
 */
export const QR_COLOR_OPTIONS: ReadonlyArray<QrColorOption> = [
  { value: QR_COLOR_BLACK, label: 'Black (default)', swatch: '#000000' },
  { value: QR_COLOR_THEME, label: 'Match panel theme', swatch: 'var(--color-primary)' },
  ...themeConfigs.map((t) => ({
    value: t.name,
    label: t.label,
    swatch: t.previewColor,
  })),
];

/**
 * Resolve a stored preference into a concrete `#rrggbb` usable by
 * `<QRCodeCanvas fgColor>`.
 *
 * `theme` reads the live CSS custom property, which is the only way to
 * pick up a runtime theme switch — the token is set on `:root` by
 * `ThemeSync`, not known at build time. Falls back to black when the var
 * is unset or we're off the DOM (SSR / tests), since a blank fgColor
 * renders an invisible QR.
 *
 * A literal `#rrggbb` is accepted too, so a hand-set preference (or a
 * value stored before the name-keyed switch) still resolves.
 */
export function resolveQrColor(pref: string): string {
  if (pref === QR_COLOR_BLACK) return '#000000';
  if (pref === QR_COLOR_THEME) {
    if (typeof window === 'undefined' || !document?.documentElement) return '#000000';
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue('--color-primary')
      .trim();
    return raw || '#000000';
  }
  const named = themeConfigs.find((t) => t.name === pref);
  if (named) return named.previewColor;
  return /^#[0-9a-f]{6}$/i.test(pref) ? pref : '#000000';
}

/* ----------------------------------------------------------------- */
/* Contrast guard                                                     */
/* ----------------------------------------------------------------- */

function srgbToLinear(c: number): number {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

/** WCAG relative luminance. Returns 0 for an unparseable colour. */
export function relativeLuminance(hex: string): number {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return 0;
  const n = parseInt(m[1], 16);
  const r = srgbToLinear((n >> 16) & 0xff);
  const g = srgbToLinear((n >> 8) & 0xff);
  const b = srgbToLinear(n & 0xff);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two hex colours (1 → 21). */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Minimum foreground/background contrast we'll print without warning.
 *
 * ISO/IEC 18004 doesn't state a ratio, but scanner vendors converge on
 * "at least 40% reflectance difference" — roughly 3:1 in WCAG terms
 * against a white background, which is the threshold used here.
 *
 * A stricter 4.5:1 (the WCAG AA text bar) was tried first and warned on
 * 8 of the 15 accents, which made the warning read as noise rather than
 * a signal. At 3:1 only Teal (2.5:1) and Emerald (2.5:1) trip it — the
 * two that genuinely disappear into white paper. The warning is
 * advisory: it never blocks a print run.
 */
export const QR_MIN_CONTRAST = 3.0;

/** True when this fg/bg pair is likely to scan reliably in print. */
export function isQrContrastSafe(fg: string, bg = '#ffffff'): boolean {
  return contrastRatio(fg, bg) >= QR_MIN_CONTRAST;
}

/* ----------------------------------------------------------------- */
/* Hook                                                               */
/* ----------------------------------------------------------------- */

function readStored(): string {
  if (typeof window === 'undefined') return QR_COLOR_BLACK;
  try {
    return localStorage.getItem(STORAGE_KEY) || QR_COLOR_BLACK;
  } catch {
    /* private-mode / disabled storage — fall back to the default. */
    return QR_COLOR_BLACK;
  }
}

/**
 * Read + write the QR colour preference.
 *
 * `color` is the resolved `#rrggbb` ready for `fgColor`; `pref` is the
 * raw stored value so the picker can keep the `theme` sentinel selected
 * rather than snapping to whatever hex it resolved to this render.
 */
export function useQrColor(): {
  pref: string;
  color: string;
  setPref: (next: string) => void;
} {
  const [pref, setPrefState] = useState<string>(readStored);
  const [color, setColor] = useState<string>(() => resolveQrColor(readStored()));

  // Re-resolve on pref change. The `theme` sentinel additionally depends
  // on the live CSS var, so this also re-runs when a caller re-mounts
  // after a theme switch.
  useEffect(() => {
    setColor(resolveQrColor(pref));
  }, [pref]);

  const setPref = useCallback((next: string) => {
    setPrefState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* Non-fatal: the colour still applies for this session. */
    }
  }, []);

  return { pref, color, setPref };
}
